import React, { useState, useEffect } from "react";
import AdminHeader from "../Header/AdminHeader";
import AdminFooter from "../Footer/AdminFooter";
import AdminLeftMenu from "../Navbar/AdminLeftMenu";
import { MdPayment } from "react-icons/md";
import styles from "./ResellerPaymentGateway.module.css";
import Axios from "axios";
import { userloginDetails } from "../Utils/AdminAuth";
import AdvanceDataTable from "../UI/Table/AdvanceDataTable";

const ALL_GATEWAYS = [
    { id: "razorpay", name: "Razorpay", desc: "UPI, Cards, Net Banking & Wallets", tags: ["UPI", "Cards", "Net Banking"] },
    { id: "stripe",   name: "Stripe",   desc: "Global payments & subscriptions",   tags: ["Cards", "Apple Pay", "Subscriptions"] },
];

const API_URL = "https://napi3.authkey.io/api2/v1/reseller/payment_gateway";

const COLUMNS = [
    {
        name: "Gateway",
        selector: (row) => row.gateway_name,
        sortable: true,
        cell: (row) => <span style={{ fontWeight: 600 }}>{row.gateway_name}</span>,
    },
    {
        name: "Key ID",
        selector: (row) => row.key_id,
        cell: (row) => <span style={{ fontFamily: "monospace", fontSize: 13 }}>{row.key_id || "-"}</span>,
    },
    {
        name: "Key Secret",
        selector: (row) => row.key_secret,
        cell: (row) => <span style={{ fontFamily: "monospace", fontSize: 13 }}>{row.key_secret || "-"}</span>,
    },
    {
        name: "Currency",
        selector: (row) => row.currency,
        cell: (row) => <span style={{ fontWeight: 600 }}>{row.currency || "-"}</span>,
    },
    {
        name: "Min Amount",
        selector: (row) => row.min_amount,
        cell: (row) => <span>{row.min_amount || "-"}</span>,
    },
    {
        name: "Status",
        selector: (row) => row.status,
        cell: (row) => (
            <span style={{
                padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600,
                background: row.status === 1 ? "#dcfce7" : "#fee2e2",
                color: row.status === 1 ? "#16a34a" : "#dc2626",
            }}>
                {row.status === 1 ? "Active" : "Inactive"}
            </span>
        ),
    },
    {
        name: "Created",
        selector: (row) => row.created,
        sortable: true,
    },
];

const ResellerPaymentGateway = () => {
    const lStorage = userloginDetails();
    const userId   = lStorage?.user?.userId;
    const userToken = lStorage?.token;

    const [view, setView]               = useState("list");
    const [gatewayList, setGatewayList] = useState([]);
    const [fetchLoading, setFetchLoading] = useState(true);
    const [loading, setLoading]         = useState(false);
    const [alert, setAlert]             = useState({ show: false, message: "", success: false });

    // Razorpay fields
    const [keyId, setKeyId]             = useState("");
    const [keySecret, setKeySecret]     = useState("");
    const [razorpayMinAmount, setRazorpayMinAmount] = useState("");

    // Stripe fields
    const [stripeSecretKey, setStripeSecretKey]   = useState("");
    const [stripeCurrencies, setStripeCurrencies] = useState([]);
    const toggleStripeCurrency = (cur) => {
        setStripeCurrencies((prev) =>
            prev.includes(cur) ? prev.filter((c) => c !== cur) : [...prev, cur]
        );
    };
    const [stripeMinAmount, setStripeMinAmount]   = useState("");

    // Derive which gateways are already configured (by lowercase name)
    const configuredIds = gatewayList.map((g) => g.gateway_name?.toLowerCase());
    const availableGateways = ALL_GATEWAYS.filter((g) => !configuredIds.includes(g.id));

    const [selectedGateway, setSelectedGateway] = useState("razorpay");

    useEffect(() => { fetchGateways(); }, []);

    // When switching to add view, auto-select the first unconfigured gateway
    const openAddView = () => {
        if (availableGateways.length > 0) setSelectedGateway(availableGateways[0].id);
        setAlert({ show: false, message: "", success: false });
        setView("add");
    };

    const fetchGateways = async () => {
        setFetchLoading(true);
        try {
            const { data } = await Axios.post(API_URL, {
                user_id: userId,
                token: userToken,
                method: "payment_gateway_list",
            });
            if (data.success && Array.isArray(data.message) && data.message.length > 0) {
                setGatewayList(data.message);
                setView("list");
            } else {
                setView("add");
            }
        } catch {
            setView("add");
        }
        setFetchLoading(false);
    };

    const saveRazorpay = async () => {
        if (!keyId.trim() || !keySecret.trim()) {
            setAlert({ show: true, message: "Please enter both Key ID and Key Secret.", success: false });
            return;
        }
        setLoading(true);
        try {
            const payload = {
                user_id: userId,
                token: userToken,
                method: "payment_gateway_setup_razorpay",
                key_id: keyId.trim(),
                key_secret: keySecret.trim(),
                currency: "INR",
            };
            if (razorpayMinAmount.trim()) payload.min_amount = razorpayMinAmount.trim();

            const { data } = await Axios.post(API_URL, payload);
            setAlert({ show: true, message: data.message, success: data.success });
            if (data.success) {
                setKeyId(""); setKeySecret(""); setRazorpayMinAmount("");
                fetchGateways();
            }
        } catch (error) {
            const msg = error?.response?.data?.message || "Something went wrong. Please try again.";
            setAlert({ show: true, message: msg, success: false });
        }
        setLoading(false);
    };

    const saveStripe = async () => {
        if (!stripeSecretKey.trim()) {
            setAlert({ show: true, message: "Please enter the Key Secret.", success: false });
            return;
        }
        if (stripeCurrencies.length === 0) {
            setAlert({ show: true, message: "Please select at least one currency (USD or EUR).", success: false });
            return;
        }
        setLoading(true);
        try {
            const payload = {
                user_id: userId,
                token: userToken,
                method: "payment_gateway_setup_stripe",
                key_secret: stripeSecretKey.trim(),
                currency: stripeCurrencies.join(","),
            };
            if (stripeMinAmount.trim()) payload.min_amount = stripeMinAmount.trim();

            const { data } = await Axios.post(API_URL, payload);
            setAlert({ show: true, message: data.message, success: data.success });
            if (data.success) {
                setStripeSecretKey(""); setStripeMinAmount(""); setStripeCurrencies([]);
                fetchGateways();
            }
        } catch (error) {
            const msg = error?.response?.data?.message || "Something went wrong. Please try again.";
            setAlert({ show: true, message: msg, success: false });
        }
        setLoading(false);
    };

    const allConfigured = availableGateways.length === 0;

    return (
        <div className="wrapper">
            <AdminLeftMenu />
            <div className="main">
                <AdminHeader />
                <main className="content">
                    <div className="container-fluid p-0">
                        <div className={styles.pageCard}>

                            {alert.show && (
                                <div className={`alert ${alert.success ? "alert-success" : "alert-danger"} alert-dismissible`} role="alert">
                                    <button type="button" className="close" onClick={() => setAlert({ ...alert, show: false })}>
                                        <span>&times;</span>
                                    </button>
                                    {alert.message}
                                </div>
                            )}

                            {/* Header */}
                            <div className={styles.header}>
                                <div className={styles.gatewayIcon}>
                                    <MdPayment size={22} color="#16a34a" />
                                </div>
                                <div className={styles.headerContent}>
                                    <h3 className={styles.title}>Payment Gateway Configuration</h3>
                                    <p className={styles.subtitle}>Configure payment collection for your reseller panel.</p>
                                </div>
                                {view === "list" && !allConfigured && (
                                    <button
                                        className={styles.saveBtn}
                                        style={{ marginLeft: "auto", padding: "8px 20px", fontSize: 13 }}
                                        onClick={openAddView}
                                    >
                                        + Add New
                                    </button>
                                )}
                                {view === "add" && gatewayList.length > 0 && (
                                    <button
                                        className={styles.saveBtn}
                                        style={{ marginLeft: "auto", padding: "8px 20px", fontSize: 13, background: "#64748b" }}
                                        onClick={() => { setAlert({ show: false, message: "", success: false }); setView("list"); }}
                                    >
                                        ← Back to List
                                    </button>
                                )}
                            </div>

                            <hr className={styles.divider} />

                            {fetchLoading ? (
                                <div className="text-center py-4">
                                    <div className="spinner-border text-success" role="status" />
                                </div>
                            ) : view === "list" ? (
                                <AdvanceDataTable
                                    tableName="Configured Payment Gateways"
                                    tableCell={COLUMNS}
                                    tableData={gatewayList}
                                    filterstatus={true}
                                    pagination={true}
                                    isLoading={false}
                                />
                            ) : (
                                <>
                                    {/* Gateway Selection — only unconfigured gateways */}
                                    <div className={styles.section}>
                                        <h5 className={styles.sectionTitle}>Select Payment Gateway</h5>
                                        <div className={styles.gatewayGrid}>
                                            {availableGateways.map(({ id, name, desc, tags }) => (
                                                <div
                                                    key={id}
                                                    className={`${styles.gatewayCard} ${selectedGateway === id ? styles.activeGateway : ""}`}
                                                    onClick={() => setSelectedGateway(id)}
                                                >
                                                    <div className={styles.gatewayTop}>
                                                        <div>
                                                            <div className={styles.gatewayName}>{name}</div>
                                                            <div className={styles.gatewayDesc}>{desc}</div>
                                                            <div className={styles.featureList}>
                                                                {tags.map(t => <span key={t} className={styles.featureTag}>{t}</span>)}
                                                            </div>
                                                        </div>
                                                        <input type="radio" checked={selectedGateway === id} readOnly />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Credentials */}
                                    <div className={styles.section}>
                                        <h5 className={styles.sectionTitle}>
                                            {selectedGateway === "stripe" ? "Stripe" : "Razorpay"} Credentials
                                        </h5>
                                        <div className={styles.credBox}>
                                            {selectedGateway === "stripe" ? (
                                                <>
                                                    <div className={styles.fieldRow}>
                                                        <label className={styles.label}>Key Secret</label>
                                                        <input
                                                            type="password"
                                                            className={styles.input}
                                                            style={{ color: "#111827", background: "#fff", WebkitTextFillColor: "#111827" }}
                                                            placeholder="Enter Key Secret"
                                                            autoComplete="new-password"
                                                            value={stripeSecretKey}
                                                            onChange={(e) => setStripeSecretKey(e.target.value)}
                                                        />
                                                    </div>
                                                    <div className={styles.fieldRow}>
                                                        <label className={styles.label}>Currency <span style={{ color: "#94a3b8", fontWeight: 400 }}>(select one or both)</span></label>
                                                        <div className="d-flex" style={{ gap: 10 }}>
                                                            {["USD", "EUR"].map((cur) => {
                                                                const selected = stripeCurrencies.includes(cur);
                                                                return (
                                                                    <div
                                                                        key={cur}
                                                                        onClick={() => toggleStripeCurrency(cur)}
                                                                        style={{
                                                                            flex: 1, padding: "8px 0", textAlign: "center",
                                                                            borderRadius: 8, cursor: "pointer", fontWeight: 700, fontSize: 13,
                                                                            border: selected ? "2px solid #16a34a" : "2px solid #e2e8f0",
                                                                            background: selected ? "#f0fdf4" : "#f8fafc",
                                                                            color: selected ? "#16a34a" : "#64748b",
                                                                            userSelect: "none",
                                                                        }}
                                                                    >
                                                                        {selected ? "✓ " : ""}{cur}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                    <div className={styles.fieldRow}>
                                                        <label className={styles.label}>Min Amount <span style={{ color: "#94a3b8", fontWeight: 400 }}>(optional)</span></label>
                                                        <input
                                                            type="number"
                                                            className={styles.input}
                                                            style={{ color: "#111827", background: "#fff" }}
                                                            placeholder="Enter minimum amount"
                                                            value={stripeMinAmount}
                                                            onChange={(e) => setStripeMinAmount(e.target.value)}
                                                        />
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    <div className={styles.fieldRow}>
                                                        <label className={styles.label}>Key ID</label>
                                                        <input
                                                            className={styles.input}
                                                            style={{ color: "#111827", background: "#fff" }}
                                                            placeholder="Enter Key ID"
                                                            autoComplete="off"
                                                            value={keyId}
                                                            onChange={(e) => setKeyId(e.target.value)}
                                                        />
                                                    </div>
                                                    <div className={styles.fieldRow}>
                                                        <label className={styles.label}>Key Secret</label>
                                                        <input
                                                            type="password"
                                                            className={styles.input}
                                                            style={{ color: "#111827", background: "#fff", WebkitTextFillColor: "#111827" }}
                                                            placeholder="Enter Key Secret"
                                                            autoComplete="new-password"
                                                            value={keySecret}
                                                            onChange={(e) => setKeySecret(e.target.value)}
                                                        />
                                                    </div>
                                                    <div className={styles.fieldRow}>
                                                        <label className={styles.label}>Currency</label>
                                                        <input
                                                            className={styles.input}
                                                            style={{ color: "#111827", background: "#f8fafc" }}
                                                            value="INR"
                                                            readOnly
                                                        />
                                                    </div>
                                                    <div className={styles.fieldRow}>
                                                        <label className={styles.label}>Min Amount <span style={{ color: "#94a3b8", fontWeight: 400 }}>(optional)</span></label>
                                                        <input
                                                            type="number"
                                                            className={styles.input}
                                                            style={{ color: "#111827", background: "#fff" }}
                                                            placeholder="Enter minimum amount"
                                                            value={razorpayMinAmount}
                                                            onChange={(e) => setRazorpayMinAmount(e.target.value)}
                                                        />
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    <div className={styles.saveWrapper}>
                                        <button
                                            className={styles.saveBtn}
                                            onClick={selectedGateway === "razorpay" ? saveRazorpay : saveStripe}
                                            disabled={loading}
                                        >
                                            {loading ? "Saving..." : "Save Configuration"}
                                        </button>
                                    </div>
                                </>
                            )}

                        </div>
                    </div>
                </main>
                <AdminFooter />
            </div>
        </div>
    );
};

export default ResellerPaymentGateway;
